import { platformApiClient } from '@/services/platform-api-client';

/**
 * Phase 12.7b Wave 1 — Monitoring + Background Jobs API.
 *
 * Both surfaces bind to endpoints that already exist or were added by this
 * wave's scoped backend work. `platformApiClient`'s response interceptor already
 * unwraps `{ data, meta }`, so `r.data` IS the payload — a second `.data` access
 * would yield `undefined`.
 */

// ── Monitoring (W1-Q1 = A · live-only) ──────────────────────────────────────

export type HealthStatus = 'healthy' | 'degraded' | 'down';

export interface PlatformHealthProbe {
  status: HealthStatus;
  latencyMs?: number;
  message?: string;
}

export interface PlatformHealth {
  checkedAt: string;
  overall: HealthStatus;
  services: Record<string, PlatformHealthProbe>;
  apiResponseMs: number;
}

/**
 * Reads the health endpoint that Phase 12.2C already deployed — the same one the
 * Dashboard uses. No second health engine, no new endpoint.
 *
 * The path is `/platform/health`, NOT `/platform/dashboard/health`:
 * `PlatformDashboardController` is mounted at `@Controller('platform')` and
 * declares `@Get('health')`, so only its *summary* route carries the `dashboard/`
 * segment. Verified against the deployed route table, not inferred from the file
 * name — the mocked unit tests cannot catch a wrong URL, and an earlier draft of
 * this client got it wrong and 404'd.
 */
export const PLATFORM_HEALTH_PATH = '/platform/health';

export async function fetchPlatformHealth(signal?: AbortSignal): Promise<PlatformHealth> {
  const r = await platformApiClient.get(PLATFORM_HEALTH_PATH, {
    ...(signal ? { signal } : {}),
  });
  const body = r.data as Partial<PlatformHealth> | null | undefined;
  return {
    checkedAt: typeof body?.checkedAt === 'string' ? body.checkedAt : '',
    overall: (body?.overall ?? 'down') as HealthStatus,
    // Guarded: a malformed body must render as "no probes", never crash the page
    // into a blank screen that could be misread as "everything is fine".
    services:
      body?.services !== null && typeof body?.services === 'object'
        ? (body.services as Record<string, PlatformHealthProbe>)
        : {},
    apiResponseMs: typeof body?.apiResponseMs === 'number' ? body.apiResponseMs : 0,
  };
}

// ── Background Jobs (W1-Q2 = A · rolling window · W1-Q3 = B · retry) ────────

export interface PlatformJobQueue {
  name: string;
  hasProcessor: boolean;
  waiting: number;
  active: number;
  completed: number;
  failed: number;
  delayed: number;
  paused: number;
}

export interface PlatformJobQueueList {
  queues: PlatformJobQueue[];
  /** The BullMQ retention window. Rolling, not durable history. */
  retention: { completed: number; failed: number };
}

/**
 * One failed job — operational metadata only.
 *
 * **There is deliberately no payload field.** OD-6 is closed: tenant business
 * payloads do not belong in the Platform Console, and the API never returns one.
 */
export interface PlatformFailedJob {
  queue: string;
  id: string;
  name: string;
  attemptsMade: number;
  maxAttempts?: number;
  timestamp?: number;
  processedOn?: number;
  finishedOn?: number;
  failedReason?: string;
  /** False when the queue has no processor — retry is refused server-side. */
  retryable: boolean;
}

export interface PlatformFailedJobPage {
  items: PlatformFailedJob[];
  total: number;
  limit: number;
  offset: number;
}

export async function fetchJobQueues(signal?: AbortSignal): Promise<PlatformJobQueueList> {
  const r = await platformApiClient.get('/platform/jobs/queues', {
    ...(signal ? { signal } : {}),
  });
  const body = r.data as Partial<PlatformJobQueueList> | null | undefined;
  return {
    queues: Array.isArray(body?.queues) ? (body.queues as PlatformJobQueue[]) : [],
    retention:
      body?.retention !== null && typeof body?.retention === 'object'
        ? (body.retention as { completed: number; failed: number })
        : { completed: 0, failed: 0 },
  };
}

export async function fetchFailedJobs(
  query: { limit?: number; offset?: number; queue?: string },
  signal?: AbortSignal,
): Promise<PlatformFailedJobPage> {
  const params: Record<string, string | number> = {};
  if (query.limit !== undefined) params['limit'] = query.limit;
  if (query.offset !== undefined) params['offset'] = query.offset;
  if (query.queue !== undefined && query.queue !== '') params['queue'] = query.queue;

  const r = await platformApiClient.get('/platform/jobs/failed', {
    params,
    ...(signal ? { signal } : {}),
  });
  const body = r.data as Partial<PlatformFailedJobPage> | null | undefined;
  return {
    items: Array.isArray(body?.items) ? (body.items as PlatformFailedJob[]) : [],
    total: typeof body?.total === 'number' ? body.total : 0,
    limit: typeof body?.limit === 'number' ? body.limit : (query.limit ?? 20),
    offset: typeof body?.offset === 'number' ? body.offset : (query.offset ?? 0),
  };
}

/**
 * Retry a failed job.
 *
 * Requires `platform:jobs:manage`, enforced **server-side**. The UI hides the
 * control for actors without it, but hiding is presentation — the backend
 * refuses regardless.
 */
export async function retryJob(queue: string, id: string): Promise<void> {
  await platformApiClient.post(
    `/platform/jobs/${encodeURIComponent(queue)}/${encodeURIComponent(id)}/retry`,
    {},
  );
}
